/**
 * Sign in — the first screen anyone sees, and the one a buyer judges the rest by.
 *
 * Composition. One column, on the page's own centre axis. There is no brand
 * panel any more, and that is the whole point of this revision.
 *
 * The panel's last form was already down to a 32px lockup and one nine-word
 * line, and it still spent 547×900 (38% of a 1440 viewport) and 560×1080 (29%
 * of 1920) to carry 10,861px² of ink — 2.2% of the pane it sat in. Shrinking it
 * had also moved the number that matters the wrong way: the form column at
 * 400px inside the remaining 892.8×800 pane measured 21.1% ink-to-pane against
 * 34.0% before, and the page ended up with two organising verticals — the
 * panel's copy at x=24 and the form centred at x=993.6 — neither of which was
 * the page centre at 720. Stripe, Linear and Vercel spend zero pixels on a
 * slogan or an illustration and centre their one column on the page's axis.
 * This screen now does the same: header rail, centred column, legal footer,
 * all three held to `--w-auth-form` so the lockup, the theme toggle, every
 * field, the CTA and the copyright share exactly two verticals, and those two
 * verticals are symmetric about the page centre at every width.
 *
 * What went with the panel: `--af-panel-*`, `--af-motif-*` and the chevron
 * motif they painted. The motif was a `preserveAspectRatio="slice"` graphic
 * whose relationship to its pane was slice arithmetic — a contained figure with
 * a 9.1px inset at 1440 and a full bleed at a heavier stroke at 1920 — and
 * whose strokes measured 1.09–1.12:1 against the ground over 87% of their
 * length against a 1.30:1 floor. Neither number was chosen. Deleting the pane
 * they lived in settles both.
 *
 * Width. `--w-auth-form` (400px) is the one measure. 520px was a 14:1 field
 * shell holding one 18px glyph; 400 is Linear's, and Stripe's is smaller still.
 *
 * Colour. Red appears on this screen only when something is wrong. The two
 * required-field asterisks are gone — every field on a two-field sign-in is
 * required, so the marks carried no information while permanently spending the
 * screen's only semantic hue. Gold appears twice: the monogram (the brand mark,
 * which declares itself) and the submit, which is the only action on the page.
 *
 * The screen never moves: both fields reserve their message row, and the status
 * region above the form is reserved at the height of the one message it holds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';

import './auth.css';

import { AlphaMark } from '@/components/app/Sidebar';
import { ThemeToggle } from '@/components/app/ThemeToggle';
import { Button, Callout, Checkbox, Field, IconButton, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/constants';
import { hasSignedInBefore, useLogin, useSession } from '@/lib/queries';
import { useLocalStorage } from '@/lib/useLocalStorage';
import { ResetAccessDialog } from './ResetAccessDialog';

const DEMO_EMAIL = 'admin@alphaform.com';
const DEMO_PASSWORD = 'alphaform';

/**
 * The demo affordance is an explicit opt-in (`VITE_DEMO_MODE=1 npm run dev`),
 * not a `DEV` default: gated on `DEV` it shipped in every review screenshot and
 * in none of production, so the composition being judged was never the one that
 * ships. It never renders in a production build and never puts a credential on
 * screen.
 */
const SHOW_DEMO = import.meta.env.VITE_DEMO_MODE === '1';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The one monogram size this screen draws — on the header rail. */
const MARK_COMPACT = 28;

/** Remembering the address is the part of "remember me" this build can honour. */
const REMEMBERED_EMAIL_KEY = 'af.auth.email';

const ERROR_ID = 'af-signin-error';

/**
 * Quiet inline link, used by the footer's legal row and by `Forgot password?`.
 *
 * Colour, decoration and focus only — no size. The two call sites are set on
 * different baselines (13px beside the Remember row, 12px in the footer's legal
 * row), and a constant that declares a size every caller has to override is a
 * constant that lies about what it produces.
 *
 * No `outline-offset` of its own: the screen had two focus offsets on eight tab
 * stops, and the base layer's `:focus-visible` already emits the 2px ring at the
 * 1px offset every shared control uses.
 */
const LINK = [
  // The 24px floor on a touch screen; the ink is unchanged. See mobile.css.
  'af-tap-line',
  'rounded-[var(--radius-xs)] text-muted underline decoration-transparent underline-offset-2',
  'transition-[color,text-decoration-color] ease-[var(--ease-out)] duration-[var(--dur-fast)]',
  'hover:text-secondary hover:decoration-current',
  'focus-visible:outline-2 outline-[color:var(--focus)]',
].join(' ');

const LEGAL_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Privacy', href: 'https://alphaformgroup.com/privacy' },
  { label: 'Terms', href: 'https://alphaformgroup.com/terms' },
  { label: 'Security', href: 'https://alphaformgroup.com/security' },
];

/** Only ever bounce back to a path inside this app. */
function safeRedirect(value: unknown): string {
  if (typeof value !== 'string') return ROUTES.inbox;
  if (!value.startsWith('/') || value.startsWith('//')) return ROUTES.inbox;
  if (value.startsWith(ROUTES.login)) return ROUTES.inbox;
  return value;
}

/** Kept short on purpose: the status band is one line, and it does not grow. */
function messageFor(error: unknown): string {
  if (ApiError.is(error)) {
    if (error.status === 401) return 'Email or password is incorrect.';
    if (error.status === 429) return 'Too many attempts. Try again shortly.';
    return error.message;
  }
  return 'Sign-in failed. Check your connection.';
}

/**
 * The field frame this screen overrides.
 *
 * `fieldShell()` grounds every control in `--surface-1` — the page's own colour
 * — behind a `--line` hairline that composites to 1.31:1. On a screen whose
 * entire job is two text inputs, that made the inputs the least visible objects
 * on it. Here they take a ground of their own and a boundary at ~3:1. The
 * invalid state keeps the danger edge untouched.
 *
 * Focus. The shared frame indicates focus with a border swap plus a 1px *inset*
 * ring — a third focus idiom next to the outline every button, checkbox and link
 * on this screen draws. Here the inset ring is traded for the same 2px outline
 * at the same 1px offset, so all eight tab stops speak one language.
 *
 * And one hue. The ring used to take `--danger` while the field was invalid,
 * which drew focus and invalidity in the same colour: after a failed empty
 * submit both fields are red and the caret is in the first one, and the whole
 * difference between "you are typing here" and "this one is also wrong" was 2px
 * of the same red. Column-scanned across each shell's top edge, focused+invalid
 * read [bg, bg, bg, danger, danger, bg, danger, field] against unfocused+invalid
 * [bg ×6, danger, field]. The field already says invalid twice — a danger border
 * and a danger message under it; the ring's only job is to say where the caret
 * is, so it is `--focus` in every state, like the other seven tab stops.
 */
function fieldFrame(invalid: boolean, extra?: string): string {
  return cn(
    'bg-[var(--surface-2)]',
    !invalid && 'border-[color:var(--af-field-line)] hover:border-[color:var(--af-field-line-strong)]',
    'focus-within:shadow-none focus-within:outline-2 focus-within:outline-offset-1',
    'focus-within:outline-[color:var(--focus)]',
    extra,
  );
}

export function LoginPage() {
  const location = useLocation();
  // Probe the session only when this browser has signed in before — otherwise
  // it is a guaranteed 401 in front of the form. See `hasSignedInBefore`.
  const [probeSession] = useState(hasSignedInBefore);
  const session = useSession({ enabled: probeSession });
  const login = useLogin();

  const [rememberedEmail, setRememberedEmail] = useLocalStorage<string>(REMEMBERED_EMAIL_KEY, '');
  const [email, setEmail] = useState(rememberedEmail);
  const [remember, setRemember] = useState(rememberedEmail.length > 0);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  // The recovery dialog is opened through a controlled prop, not a DialogTrigger,
  // so Radix has nothing to hand focus back to when it closes. This is that node.
  const forgotRef = useRef<HTMLButtonElement>(null);

  const pending = login.isPending;
  const failed = login.isError;

  const redirectTo = useMemo(
    () => safeRedirect((location.state as { from?: unknown } | null)?.from),
    [location.state],
  );

  const emailError = !email.trim()
    ? 'Enter your work email.'
    : EMAIL_PATTERN.test(email.trim())
      ? null
      : 'That does not look like an email address.';
  const passwordError = password.length === 0 ? 'Enter your password.' : null;

  // A field only shows its error once the form has been attempted.
  const showEmailError = submitted ? emailError : null;
  const showPasswordError = submitted ? passwordError : null;

  useEffect(() => {
    document.title = 'Sign in · AlphaForm CRM';
  }, []);

  // A rejected attempt must not drop focus on the document: park it on the
  // message, one Tab away from the fields it is about.
  useEffect(() => {
    if (failed) statusRef.current?.focus();
  }, [failed, login.error]);

  /** Both inputs point at the form-level message while one is showing. */
  const describedBy = useCallback(
    (own: string | undefined): string | undefined => {
      const ids = [own, failed ? ERROR_ID : null].filter(Boolean);
      return ids.length > 0 ? ids.join(' ') : undefined;
    },
    [failed],
  );

  if (session.data) return <Navigate to={redirectTo} replace />;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    if (emailError || passwordError) {
      // The keyboard path matches the server-failure path: something takes
      // focus, and it is the thing that needs fixing.
      document.getElementById(emailError ? 'af-email' : 'af-password')?.focus();
      return;
    }
    setRememberedEmail(remember ? email.trim() : '');
    login.mutate({ email: email.trim(), password });
  };

  /** Esc on the focused status message clears it and hands the caret back. */
  const dismissError = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    login.reset();
    document.getElementById('af-password')?.focus();
  };

  const useDemo = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    setSubmitted(false);
    login.reset();
  };

  return (
    // Full bleed, no cap and no shadow, and no second pane. Three rows — a
    // `--h-topbar` rail, the column centred in everything that is left, and a
    // `--h-auth-footer` closing band — each holding its contents to
    // `--w-auth-form`, so the page has exactly two verticals and they are
    // symmetric about its centre.
    <div className="af-auth flex h-full w-full flex-col bg-[var(--surface-1)]">
      {/* Same height as the app's own top bar, so the two chromes agree. The
          row's contents are held to the sign-in column's own edges: the
          lockup's left edge is the h1's, and the theme toggle's right edge is
          the CTA's. */}
      <header className="flex h-[var(--h-topbar)] shrink-0 items-center px-6">
        <div className="mx-auto flex w-full max-w-[var(--w-auth-form)] items-center justify-between gap-3">
          {/* The lockup, at every width — it used to be suppressed above `lg`
              because a brand panel carried a second copy of it. With the panel
              gone this is the only one, so it is unconditional. It used to read
              "AlphaForm" over "Group CRM", which was a sixth spelling of the
              product on a screen that now has one: the company is "AlphaForm
              Group" and the product is "AlphaForm CRM", here as everywhere
              else. */}
          <span className="flex items-center gap-2">
            <AlphaMark size={MARK_COMPACT} />
            <span className="flex flex-col leading-none">
              <span className="text-base font-semibold tracking-[var(--tracking-tight)] text-primary">
                AlphaForm Group
              </span>
              <span className="eyebrow mt-1">CRM</span>
            </span>
          </span>
          <ThemeToggle size="sm" />
        </div>
      </header>

      {/* Centred between the header and the footer, with nothing added on
          either side of the centring: `pb-12` here bought a 24px optical lift
          by *paying* for it below, which is the one direction this screen
          cannot afford — the void under the last control was already the
          largest empty area on it (233.7px against 185.6px above). The two
          voids are now equal at every height. */}
      <main className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-6">
        <div className="w-full max-w-[var(--w-auth-form)]">
          {/* The heading names the destination; the CTA below says the verb.
              Both saying "Sign in" spent the one line that can tell the user
              what they are signing in to.

              And the destination has ONE name. The screen used to print five
              in 900 vertical pixels — "AlphaForm Group" on the lockup, "CRM"
              on the eyebrow, "AlphaForm" here, "your AlphaForm workspace
              account" below, "AlphaForm CRM" on the tab — so a reader could
              not tell whether they were signing in to a company, a product or
              a workspace. The product is `AlphaForm CRM`, here and on the tab
              title. "AlphaForm Group" is the company, and it keeps the lockup
              and the copyright, which is where a company name belongs. The
              word "workspace" is gone from the column entirely. */}
          {/* `leading-none` on the h1 — a single display line with nothing
              under it inside its own box — and `leading-tight` on the subhead,
              whose descenders were painting outside a 14px/14px box that the
              16px above it and the 12px below it are measured from. The steps
              in this column are box steps on the 4px grid, each produced by
              one named declaration; that is the claim, and it is not the same
              claim as optical evenness. */}
          <h1 className="text-3xl leading-none font-semibold tracking-[var(--tracking-tight)] text-primary">
            Sign in to AlphaForm CRM
          </h1>

          {/* One reserved region for the standing subhead and for a form-level
              failure — the form below it never moves. Top-aligned, not centred:
              the subhead is the h1's, so it sits --space-4 under it, while the
              Callout fills the band and lands --space-3 above the fields it is
              about. Proximity now points at the thing each message belongs to.

              The reservation is the Callout's own compact height, declared
              once beside the Callout's own compact padding and applied by the
              Callout itself as a `min-h` — so the band is exactly full when
              it holds the failure, and a pixel of drift in the Callout moves
              the reserve with it. It used to be `--h-auth-status: 35px`, a
              type metric transcribed into a layout token: correct on the day
              it was measured and silently wrong on any day after. 16 above
              and 12 below, both named.

              The band measures 36.0 (`--h-callout-compact`, now `--space-2` ×2
              + `--space-5` — it carried a bare `+ 3px` and computed to 35).
              The subhead's line box is 16.1 of that, so 19.9 of reserve stands
              unpainted under it while the form is clean. That is deliberate and
              it is what keeps the column still, but it means the step the eye
              sees below the subhead is not the 12 the margin names — see the
              rhythm table below, which states both. */}
          <div className="mt-4 flex min-h-[var(--h-callout-compact)] flex-col justify-start">
            {failed ? (
              // The ref and the roving tabindex live on the Callout, which
              // keeps its own `role="alert"`: an element can be both an alert
              // and a programmatic focus target, and the caller's old
              // `role="group"` silently removed the only announcement path.
              // The ring is asked for on `:focus` (a programmatic focus never
              // matches `:focus-visible`) and takes the danger hue — the blue
              // one made the error box read as an active field.
              //
              // The band no longer fires on an incomplete submit: two empty
              // fields already paint two red borders and two messages, and a
              // third line saying "check the highlighted fields" added nothing
              // while evicting the standing subhead. It is now reserved for the
              // failures a field cannot express — 401, 429, no connection.
              <Callout
                ref={statusRef}
                id={ERROR_ID}
                tabIndex={-1}
                variant="danger"
                compact
                // Every other dismissible surface in the product answers Esc.
                // Here that means: clear the failure and put the caret on the
                // field that has to change.
                onKeyDown={dismissError}
                className="border-[color-mix(in_srgb,var(--danger)_60%,transparent)] focus:outline-2 focus:outline-offset-1 focus:outline-[color:var(--danger)]"
              >
                <span className="line-clamp-2">{messageFor(login.error)}</span>
              </Callout>
            ) : (
              <p className="text-md leading-tight text-muted">
                Use the account your administrator issued.
              </p>
            )}
          </div>

          {/* One rhythm. Every value below is a Playwright rect, not an
              intention, and every one is produced by a single named
              declaration rather than by a sum:

                h1 → status band              mt-4      --space-4   (16.0)
                status band → Email label     mt-3      --space-3   (12.0)
                label → its field             gap-1     --space-1   ( 4.0)
                field → its message           gap-1     --space-1   ( 4.0)
                message → next label          gap-4     --space-4   (16.0)
                message → the checkbox row    mt-4      --space-4   (16.0)
                row → submit                  mt-6      --space-6   (24.0)
                submit → invite copy          mt-3      --space-3   (12.0)

              Those are BOX steps, and the first two are the only two the eye
              does not read off the boxes, because the status band is 36.0 tall
              and the subhead standing in it paints 16.1 of that from the top.
              Ink-to-ink, scanned row-by-row at 0.5px across the column's x
              range on the shipped 1440×900 PNG (h1 265.5→291.5, subhead
              308.5→321.5, Email label 356.0→365.5):

                h1 → subhead                  17.0 ink-to-ink
                subhead → Email label         mt-3 (12.0) + 19.9 reserve
                                              = 34.5 ink-to-ink

              The reserve is the point of the band — it is what stops the form
              moving when a 401 lands — so the fix is to state it, not to spend
              it. `justify-end` would hand the 19.9 to the h1 gap instead and
              buy a truthful 12 below at the cost of separating the subhead
              from the heading it belongs to; the proximity is worth more than
              the round number.

              The two steps that used to be described as `--space-8` were not
              one margin at all — `--space-8` appears nowhere in this file.
              Each was 6 + 16 + 8 + a 2px label inset, assembled from four
              places, so tuning the token named in the comment did nothing and
              tuning the gap that was not named did everything. They are the
              same two steps as before in *kind* — a field shell to the next
              thing — and each now measures 4 (field gap) + 16 (the reserved
              message row) + 16 (--space-4) = 36.0, the same on both.

              What the change buys is proximity. In the state a failing user
              actually sees, a red message sat 6px under the input it was
              about and 8px above the label of the next field: ownership of a
              12px line decided by 2px, with neither 6 nor 10 on the 4px grid.
              It is 4 against 16 now — a 4:1 ratio, both ends named. */}
          <form onSubmit={onSubmit} noValidate className="mt-3">
            <div className="flex flex-col gap-4">
              {/* `required` sits on the input, not on the Field: the semantic
                  reaches assistive tech without painting an asterisk that says
                  nothing on a form where both fields are required. */}
              <Field label="Email" htmlFor="af-email" error={showEmailError} className="gap-1">
                {({ id, describedBy: own, invalid }) => (
                  <Input
                    id={id}
                    name="email"
                    type="email"
                    size="lg"
                    required
                    autoComplete="username"
                    autoFocus
                    spellCheck={false}
                    autoCapitalize="none"
                    // No placeholder: the visible label, `type="email"` and
                    // `autoComplete="username"` already say what belongs here,
                    // and a fabricated address inside an autofocused blue frame
                    // made the first thing on the screen look like a filled field.
                    iconLeft={<Mail />}
                    containerClassName={fieldFrame(invalid)}
                    value={email}
                    invalid={invalid}
                    disabled={pending}
                    aria-describedby={describedBy(own)}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Password"
                htmlFor="af-password"
                error={showPasswordError}
                className="gap-1"
              >
                {({ id, describedBy: own, invalid }) => (
                  // The reveal sits *after* the input in the DOM, so tabbing
                  // from the email field lands on the password field itself.
                  <div className="relative">
                    <Input
                      id={id}
                      name="password"
                      type={revealed ? 'text' : 'password'}
                      size="lg"
                      required
                      autoComplete="current-password"
                      iconLeft={<Lock />}
                      containerClassName={fieldFrame(invalid, 'pr-8')}
                      value={password}
                      invalid={invalid}
                      disabled={pending}
                      aria-describedby={describedBy(own)}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <IconButton
                      label={revealed ? 'Hide password' : 'Show password'}
                      variant="ghost"
                      size="sm"
                      aria-pressed={revealed}
                      aria-controls={id}
                      disabled={pending}
                      onClick={() => setRevealed((value) => !value)}
                      className="absolute top-1/2 right-1 -translate-y-1/2"
                    >
                      {revealed ? <EyeOff /> : <Eye />}
                    </IconButton>
                  </div>
                )}
              </Field>
            </div>

            {/* Both ends of this row land on the column's own edges — nothing
                on this screen is centred under something else. The row's box
                *is* its 16px line box (`h-4`), and its margin is the same
                plain `--space-4` the field wrapper's gap is, so this row and
                the `Password` label start on the same 36.0 step below the
                message row above them. The margin used to be spelled
                `--space-2 + --af-label-inset`: a *label* inset added to a row
                that has no label, landing on the right number for the wrong
                reason.

                Both items are set at `--text-sm`, the size the field labels
                and the invite copy take. At `--text-base` they were a third
                small size 1px off the other two — three sizes inside a 2px
                band in one 380px column. */}
            <div className="mt-4 flex h-4 items-center justify-between gap-3">
              <Checkbox
                size="sm"
                checked={remember}
                disabled={pending}
                onCheckedChange={(value) => setRemember(value === true)}
                label="Remember my email"
                // Secondary, the tier the `Email` and `Password` labels use.
                // At the component's default `text-primary` an optional,
                // unchecked preference was the second-loudest text in the
                // column — 16.09:1, the h1's own ink — and louder than the
                // labels of the two fields that actually have to be filled.
                labelClassName="text-sm text-secondary"
              />
              {/* Quiet, like the footer's legal links — same ink and the same
                  size, which is also the size of the labels of the two fields
                  it sits under.

                  The gold census, counted on the screen and not in the
                  intention: the monogram, the `CRM` eyebrow, the motif's
                  strokes and this form's submit — four, of which three are
                  brand marks inside the panel and one is the only action on
                  the page — and the only gold *fill* on it is the submit
                  (measured on the live DOM at every review width, in both
                  themes). The proof list's three chips used to make it six;
                  they carried no meaning and they are gone with the list. The
                  escape hatch has never been allowed the hue. */}
              <button
                ref={forgotRef}
                type="button"
                onClick={() => setRecovering(true)}
                className={cn(LINK, 'shrink-0 text-sm leading-4')}
              >
                Forgot password?
              </button>
            </div>

            <div className="mt-6 flex flex-col">
              <Button
                type="submit"
                variant="primary"
                // The control that ends the task gets a step of mass the
                // controls that feed it do not: 40px against the fields' 36.
                // Hue alone was carrying the whole distinction. That step is
                // `--h-control-xl`, on the control scale — spelled
                // `h-[var(--space-10)]` it was a control dimension borrowed
                // from the spacing ramp, and any change to the ramp resized
                // the CTA.
                size="xl"
                fullWidth
                loading={pending}
                // The screen's only action must not lose its only word while
                // it is working. With no left icon a loading Button hides its
                // label and centres a 14px spinner in the box, which on a
                // 520px gold bar reads as an empty control. `loadingLabel`
                // moves the spinner into the icon slot and swaps the verb for
                // its progressive — same 520×40 footprint, and the button
                // still says what it is doing.
                loadingLabel="Signing in…"
                className="font-semibold"
              >
                Sign in
              </Button>

              {/* The other question this screen has to answer. Flush with the
                  column, like everything else in it. One margin, not a
                  container gap plus a child margin: 12px was the only step in
                  this column that could not be read off either declaration
                  alone. */}
              <p className="mt-3 text-sm leading-normal text-muted">
                No account yet? Your administrator sends the invite.
              </p>

              {/* Never a second full-width control under the CTA: two identical
                  boxes read as a choice and halve the primary's dominance. */}
              {SHOW_DEMO ? (
                <div className="mt-2 flex justify-center">
                  <Button variant="ghost" size="sm" disabled={pending} onClick={useDemo}>
                    Use demo workspace
                  </Button>
                </div>
              ) : null}
            </div>
          </form>
        </div>
      </main>

      {/* No rule above it: the legal row is quiet enough to sit on open ground,
          and a hairline across a page with nothing on either side of the column
          would be the only horizontal on the screen that divides nothing.

          `--h-auth-footer` rather than `py-4`, and `--h-topbar` on the header,
          because the two bands are what the centred column is centred between:
          naming both means the column's optical centre is a consequence of two
          declarations rather than of padding. `py-2` is only the floor for the
          day the legal row wraps. */}
      <footer className="flex min-h-[var(--h-auth-footer)] shrink-0 items-center px-6 py-2">
        <div className="mx-auto flex w-full max-w-[var(--w-auth-form)] flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p className="text-sm text-faint">© 2026 AlphaForm Group</p>
          <nav aria-label="Legal" className="flex items-center gap-4">
            {LEGAL_LINKS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className={cn(LINK, 'text-sm')}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </footer>

      <ResetAccessDialog
        open={recovering}
        initialEmail={email}
        onOpenChange={setRecovering}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          forgotRef.current?.focus();
        }}
      />
    </div>
  );
}

export default LoginPage;
